import React, { createContext, useContext, ReactNode } from "react";
import { Socket } from "socket.io-client";
import IUserUI from "../models/IUserUI";
import IVendorModel from "../models/IVendorModel";
import ISupplierModel from "../models/ISupplierModel";
import { IUiContextModel } from "./UiContext";
import UserContext from "./UserContext";
import VendorContext from "./VendorContext";
import SupplierContext from "./SupplierContext";
import UiContext from "./UiContext";
import { SocketContext } from "./SocketContext";

export interface IAppContext {
    userModel: IUserUI;
    vendorModel: IVendorModel;
    supplierModel: ISupplierModel;
    uiContext: IUiContextModel;
    socket: Socket | null;
}

const AppContext = createContext<IAppContext | undefined>(undefined);

export interface AppContextProviderProps {
    userModel: IUserUI;
    vendorModel: IVendorModel;
    supplierModel: ISupplierModel;
    uiContext: IUiContextModel;
    socket: Socket | null;
    children: ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({
    userModel,
    vendorModel,
    supplierModel,
    uiContext,
    socket,
    children,
}) => {
    const appContextValue: IAppContext = {
        userModel,
        vendorModel,
        supplierModel,
        uiContext,
        socket,
    };

    return (
        <AppContext.Provider value={appContextValue}>
            <UiContext.Provider value={uiContext}>
                <SupplierContext.Provider value={supplierModel}>
                    <UserContext.Provider value={userModel}>
                        <VendorContext.Provider value={vendorModel}>
                            <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
                        </VendorContext.Provider>
                    </UserContext.Provider>
                </SupplierContext.Provider>
            </UiContext.Provider>
        </AppContext.Provider>
    );
};

export const useAppContext = (): IAppContext => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error("useAppContext must be used within an AppContextProvider");
    }
    return context;
};

export interface IAppContextNonNull extends Omit<IAppContext, "socket"> {
    socket: Socket;
}

export const useAppContextNonNull = (): IAppContextNonNull => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error("useAppContextNonNull must be used within an AppContextProvider");
    }
    if (context.socket === null) {
        throw new Error("Socket is null. useAppContextNonNull should only be used when socket is guaranteed to be initialized");
    }
    return context as IAppContextNonNull;
};

export default AppContext;
